import { useCallback } from 'react'
import { getModifierThumbSrc } from 'public/components/online_ordering/modifierUtils'
import { WrappedModifier } from 'src/public/components/online_ordering/types'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'
import { ItemSource } from './cartUtils'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { useTimeBasedRules } from 'src/public/components/online_ordering/TimeBasedRuleContext'
import { useMenuSearchContext } from '../../search'

type AddToCartResponse = {
  success: boolean
  errorType: null | string
}

export const useAddToCartHandler = () => {
  const { addToCart } = useCart()
  const { fulfillmentData } = useFulfillment()
  const { timeBasedRulesMap } = useTimeBasedRules()
  const { searchString, canUseSearch } = useMenuSearchContext()

  const handleAddToCart = useCallback(
    async (
      wrappedModifier: WrappedModifier,
      quantity: number,
      channelGuid: string,
      itemSource: ItemSource,
      handleError: (error: any) => void,
      handleAddToCartSuccess?: () => void,
      additionalTrackData?: any,
      isReorder?: boolean
    ): Promise<AddToCartResponse> => {
      if (!wrappedModifier) {
        return { success: false, errorType: null }
      }

      let errorType = null
      const timeBasedRules =
        timeBasedRulesMap[wrappedModifier.modifier.itemGuid]

      // Counts only top-level modifier group members; nested modifier groups are not traversed
      const modifierGroups = wrappedModifier.modifier.modifierGroups || []
      const allModifiers = modifierGroups.flatMap((g) => g.modifiers || [])
      const modifiersWithImages = allModifiers.filter(
        (m) => !!getModifierThumbSrc(m)
      )
      const hasModifierImages = modifiersWithImages.length > 0
      const modifierImageCoverage =
        allModifiers.length > 0
          ? Math.round((modifiersWithImages.length / allModifiers.length) * 100)
          : null

      const trackData = {
        quantity,
        unitOfMeasure: wrappedModifier?.unitOfMeasure,
        timeBasedRule: timeBasedRules?.leadTimeRule
          ? 'Minimum lead time'
          : timeBasedRules?.preorderRule
          ? 'Preorder'
          : timeBasedRules?.pickupWindowRule
          ? 'Pickup window'
          : null,
        canUseSearch,
        searchString: canUseSearch && searchString ? searchString : null,
        isReorder: isReorder || false,
        hasModifiers: wrappedModifier.modifier.modifierGroups.length > 0,
        hasModifierImages,
        modifierImageCoverage,
        ...additionalTrackData
      }

      try {
        errorType = await addToCart(
          wrappedModifier,
          quantity,
          fulfillmentData?.cartFulfillmentData,
          trackData,
          channelGuid,
          itemSource
        )

        if (errorType === null) {
          handleAddToCartSuccess && handleAddToCartSuccess()
        }
      } catch (error) {
        handleError(error)
      }

      return { success: errorType === null, errorType }
    },
    [addToCart, fulfillmentData, timeBasedRulesMap, canUseSearch, searchString]
  )

  return handleAddToCart
}
